import { ChangeEvent } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { CenteredButton, Chip, QuestionTooltip } from '@app/core';
import { FormikValues, useField, useFormikContext } from 'formik';
import { Offer } from '@app/api';
import { CheckboxBlockField } from '@app/form';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getSimpleOfferPrice, PriceDisplayMethod } from 'lib/priceComputation';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3,
  },
  containerCheckBox: {
    alignItems: 'unset',
    padding: theme.marginBase * 3,
  },
  containerCheckBoxWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  checkBoxTextContainer: {
    paddingTop: theme.marginBase / 2,
    paddingLeft: theme.marginBase,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase / 2,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.marginBase,
  },
  title: {
    ...theme.fonts.h5,
  },
  description: {
    ...theme.fonts.caption,
  },
  chip: {
    whiteSpace: 'nowrap',
  },
  chipContainer: {
    display: 'flex',
    gap: theme.marginBase,
  },
}));

interface Props {
  offers: Offer[] | undefined;
  onClick?: (offer: Offer) => any;
  max?: number;
  className?: string;
  hideNotNeeded?: boolean;
  name?: string;
  disabledOffers?: string[];
}

export const OfferOptionBlock = ({
  offers,
  onClick,
  max,
  className,
  hideNotNeeded,
  name = 'optionsOfferIds',
  disabledOffers = [],
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const { values } = useFormikContext<FormikValues>();
  const [{ value: fieldValues }, _, { setValue }] = useField<string[]>(name);

  const onChangeOption = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    let newValue = [] as string[];
    if (fieldValues && Array.isArray(fieldValues)) {
      newValue = fieldValues;
    }
    if (fieldValues?.includes(value)) {
      newValue = fieldValues.filter((offerId: string) => offerId !== value);
    } else if (!max || max > newValue.length) {
      newValue.push(value);
    } else if (max === 1) {
      newValue = [value];
    }
    if (onClick) {
      const offer = offers?.find((offer) => offer.id === value);
      if (offer) {
        onClick(offer);
      }
    }

    setValue([...newValue]);
  };
  const duration = values?.duration || 60;
  const multiple = !max || max > 1;

  return (
    <div className={classNames(classes.container, className)}>
      {offers?.map((offer) => {
        const { amount, priceDisplayMethod } = getSimpleOfferPrice(
          offer.products,
        );
        return (
          <CheckboxBlockField
            labelClassName={classes.containerCheckBox}
            label={offer.name}
            name={name}
            value={offer.id}
            type={multiple ? 'checkbox' : 'radio'}
            multiple={multiple}
            onChange={onChangeOption}
            checked={!!fieldValues?.includes(offer.id)}
            disabled={disabledOffers.includes(offer.id)}
            trackingName="Option Offer"
            key={offer.id}
          >
            <div className={classes.containerCheckBoxWrapper}>
              <div className={classes.checkBoxTextContainer}>
                <div className={classes.titleContainer}>
                  <h5 className={classes.title}>{offer.name}</h5>
                  <QuestionTooltip title={offer.tooltip} placement="top" />
                </div>
                {offer.description && (
                  <p className={classes.description}>{offer.description}</p>
                )}
              </div>
              <div className={classes.chipContainer}>
                {priceDisplayMethod === PriceDisplayMethod.PER_PARTICIPANT ? (
                  <Chip color="blue" className={classes.chip}>
                    {t('studySetup.generalSetup.option.pricePerParticipant', {
                      count: amount,
                    })}
                  </Chip>
                ) : (
                  <Chip color="blue" className={classes.chip}>
                    {t('studySetup.generalSetup.option.price', {
                      count: amount,
                    })}
                  </Chip>
                )}
              </div>
            </div>
          </CheckboxBlockField>
        );
      })}

      {!hideNotNeeded && values.optionsOfferIds === undefined && (
        <CenteredButton
          text={t('studySetup.generalSetup.option.noNeed')}
          variant="transparent"
          onClick={() => {
            setValue([]);
          }}
          trackingName="Option Offer"
        />
      )}
    </div>
  );
};
